import * as React from "react";
import styled from "styled-components";
import { useInteractiveColor } from "@sproutsocial/seeds-react-hooks";
import Button from "@sproutsocial/seeds-react-button";
import Icon from "@sproutsocial/seeds-react-icon";

export interface TypeState {
  hasValue: boolean;
}

// Using Context so that Input's Input.ClearButton-specific props can be passed to Input.ClearButton,
// regardless of whether it is manually included as elemAfter or automatically included for type="search" Inputs.
export type TypeInputContext = Partial<{
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleClear: (e: React.SyntheticEvent<HTMLButtonElement>) => void;
  clearButtonLabel: string;
  hasValue: boolean;
  size: "large" | "small" | "default";
}>;

// Shared by both the styled-components and Tailwind paths so that a single
// ClearButton identity exists, keeping isClearButton detection and the static
// Input.ClearButton consistent regardless of which path renders.
export const InputContext = React.createContext<TypeInputContext>({});

const StyledButton = styled(Button)`
  &:hover,
  &:active {
    color: ${(props) => useInteractiveColor(props.theme.colors.icon.base)};
  }
`;

export const ClearButton = () => {
  const {
    handleClear,
    clearButtonLabel,
    hasValue,
    size: inputSize,
  } = React.useContext(InputContext);

  // Hide the button when there is no text to clear.
  if (!hasValue) {
    return null;
  }

  // Warn if clearButtonLabel is not included, so that the unlocalized fallback will not be mistaken for a proper label.
  if (!clearButtonLabel) {
    // eslint-disable-next-line no-console
    console.warn(
      "Warning: clearButtonLabel prop is required when using Input.ClearButton. Please pass a localized clearButtonLabel to Input."
    );
  }

  // Reduce Button padding for size small Inputs so that the Button won't go outside the bounds of the Input.
  // This adjustment is handled automatically for default and large Inputs via Button's size. There is no "small" Button.
  const py = inputSize === "small" ? 100 : undefined;
  const px = inputSize === "small" ? 200 : undefined;
  const buttonSize = inputSize === "small" ? "default" : inputSize;
  return (
    <StyledButton
      onClick={handleClear}
      size={buttonSize}
      py={py}
      px={px}
      title={clearButtonLabel || "Clear"}
      ariaLabel={clearButtonLabel || "Clear"}
      color="icon.base"
    >
      <Icon name="circle-x-outline" aria-hidden />
    </StyledButton>
  );
};

ClearButton.displayName = "Input.ClearButton";

// Used for positioning elementAfter. This logic will detect if the element is a ClearButton,
// regardless of whether it was manually passed as elemAfter or automatically added to a search Input.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isClearButton = (elem: any) => {
  if (elem?.type) {
    return elem.type.displayName === "Input.ClearButton";
  }

  return false;
};
